import { useEffect, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';
import * as ConversationalAiClient from '../../services/conversationalAiClient.ts';
import { SnippetMetadataError } from '../../services/conversationalAiClient.ts';
import type {
	SnippetMetadataSuggestion,
	SnippetMetadataResponse,
} from '../../services/types/conversationalAiClientTypes.ts';
import { wrapAdfWithDocNode } from '../../utils/adfUtils.ts';
import { reportError } from '../../utils/reportError.ts';

function pickBestSuggestion(suggestions: SnippetMetadataSuggestion[]): SnippetMetadataSuggestion {
	return suggestions.reduce((a, b) => (a.confidence >= b.confidence ? a : b));
}

function serializeAdf(adf: ADFEntity | ADFEntity[]): string {
	return JSON.stringify(wrapAdfWithDocNode(adf));
}

export type AiSuggestions = {
	isAiSuggestionLoading: boolean;
	aiSuggestedName: string | null;
	aiSuggestedDescription: string | null;
};

/**
 * Fetches AI-generated name and description suggestions for a snippet.
 * Fires success/failure operational analytics with timing and payload details.
 *
 * Enablement gate: AI name & description suggestions are gated behind
 * `platform_editor_blocks_ai_metadata_enabled` (EDITOR-7779 / ML-12077). The
 * gate defaults to OFF, so when it is off `fetchSnippetMetadata` is never called
 * and NO success/failure operational analytics are emitted for the skipped call.
 * The hook returns its default state (`isAiSuggestionLoading: false`,
 * `aiSuggestedName: null`, `aiSuggestedDescription: null`) so the create flow
 * degrades cleanly to its non-AI defaults. This ships disabled by default; the
 * gate can be rolled out to enable AI suggestions once the AI Gateway use-case
 * `confluence-snippets-metadata` is approved for external production.
 *
 * Benign not-ready states (ADF or cloudId not yet available, e.g. the modal
 * mounting before content is resolved) are skipped without emitting analytics,
 * so they don't inflate the operational failure count.
 */
export function useAiSuggestions(
	adf: ADFEntity | ADFEntity[] | undefined,
	origin: AnalyticsOrigin,
): AiSuggestions {
	const { cloudId } = useSessionData();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(origin);

	const [isAiSuggestionLoading, setIsAiSuggestionLoading] = useState(false);
	const [aiSuggestedName, setAiSuggestedName] = useState<string | null>(null);
	const [aiSuggestedDescription, setAiSuggestedDescription] = useState<string | null>(null);

	useEffect(() => {
		// Tracks whether this particular effect run has been superseded (adf/cloudId
		// changed) or the component has unmounted. Async state writes below are skipped
		// for stale runs so a slow in-flight request that resolves after the inputs
		// changed can't clobber fresh state or write to an unmounted component.
		let isStale = false;

		// Enablement gate (EDITOR-7779 / ML-12077): AI metadata suggestions are
		// OFF by default. Unless the gate is explicitly enabled, skip the request
		// entirely — we do NOT call fetchSnippetMetadata and emit NO success/failure
		// operational analytics for the skipped call, so unentitled tenants don't
		// see a broken AI experience and we don't flood snippetApiAiMetadata with
		// gateway-rejection failures. The hook keeps its default state, so the
		// create flow falls back to its non-AI defaults. Inlined per
		// confluence-feature-gating/inline-usage; roll the gate out to enable AI
		// once the gateway use-case is approved for external prod.
		if (!fg('platform_editor_blocks_ai_metadata_enabled')) {
			return;
		}

		if (!adf || !cloudId) {
			// The CreateSnippet modal mounts this hook before content/eligibility
			// is resolved, so ADF or cloudId can be transiently absent. These
			// benign not-ready lifecycle states are treated as non-failures and
			// skip instrumentation entirely, so they don't inflate the
			// snippetApiAiMetadata failure count.
			setIsAiSuggestionLoading(false);
			return;
		}

		setIsAiSuggestionLoading(true);
		const startTime = performance.now();
		const adfContent = serializeAdf(adf);
		const adfBodySize = adfContent.length;
		ConversationalAiClient.fetchSnippetMetadata({ adfContent }, cloudId)
			.then((response: SnippetMetadataResponse) => {
				const durationMs = Math.round(performance.now() - startTime);
				const suggestionCount = response.suggestions?.length ?? 0;

				if (!suggestionCount) {
					fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
						suggestionCount: 0,
						adfBodySize,
						durationMs,
					});
					return;
				}
				const bestSuggestion = pickBestSuggestion(response.suggestions);
				fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
					suggestionCount,
					confidences: response.suggestions.map((s) => s.confidence),
					suggestedNameLength: bestSuggestion.name?.length ?? 0,
					suggestedDescriptionLength: bestSuggestion.description?.length ?? 0,
					adfBodySize,
					durationMs,
				});
				// Skip the state write if this run was superseded mid-flight (inputs
				// changed) or the component unmounted, so stale suggestions can't
				// overwrite fresh state. Analytics above still reflect what the API
				// actually returned.
				if (isStale) {
					return;
				}
				setAiSuggestedName(bestSuggestion.name ?? '');
				setAiSuggestedDescription(bestSuggestion.description ?? '');
			})
			.catch((error) => {
				const durationMs = Math.round(performance.now() - startTime);
				fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
					error: error instanceof Error ? error.message : 'Unknown error',
					...(error instanceof SnippetMetadataError && { statusCode: error.statusCode }),
					adfBodySize,
					durationMs,
				});
				reportError(error);
			})
			.finally(() => {
				// Don't reset loading for a superseded/unmounted run; a newer run
				// owns the loading state.
				if (isStale) {
					return;
				}
				setIsAiSuggestionLoading(false);
			});

		return () => {
			// Mark this effect run stale on cleanup (inputs changed or unmount) so
			// any in-flight request that resolves later won't write state.
			isStale = true;
		};
	}, [adf, cloudId, fireFailedAnalytic, fireSuccessAnalytic]);

	return { isAiSuggestionLoading, aiSuggestedName, aiSuggestedDescription };
}
